import {
  FieldDescription,
  IFeatureLayerInfo,
  IImageLegendRowInfo,
  ISourceDescription,
  ISourceDescriptions
} from '../../interfaces/models'
import {IFeatureLayerInfos, IFieldsInfo} from '../../interfaces/reducers'
import {DeniedServiceInfo} from '../../models/DeniedServiceInfo'
import {FailedServiceInfo} from '../../models/FailedServiceInfo'
import {FeatureLayerInfo} from '../../models/FeatureLayerInfo'

import {v4 as uuidv4} from 'uuid'
import {CoordinateSystem} from '../../enums/CoordinateSystem'
import {GeometryType} from '../../enums/GeometryType'
import {ViewSchemaType} from '../../enums/ViewSchemaType'
import {createNewFeature, getResolutions, mapToBlob} from '../../helpers'
import {IStore} from '../../interfaces/reducers'
import {
  Feature,
  Geometry,
  ImageLayer,
  LineString,
  Map,
  Point,
  Polygon,
  Style,
  TileLayer,
  VectorLayer,
  VectorSource,
  View
} from '../../models'

export abstract class LayerInfosService {
  protected getFieldsInfo(
    fieldsDescription: Array<FieldDescription>,
    sourceDescription: ISourceDescription
  ): IFieldsInfo {
    const fields: IFieldsInfo = {}

    if (!fieldsDescription) {
      console.warn(`Broken - ${sourceDescription.namespacedUrl}/${sourceDescription.layerName}`)
    } else {
      fieldsDescription.forEach(f => {
        let sourceFieldInfo = sourceDescription.fieldInfos[f.name]
        if (!sourceFieldInfo) {
          sourceFieldInfo = {
            label: f.name,
            visible: f.type != 'geometry' && sourceDescription.idField != f.name
          }
        }
        fields[f.name] = {
          ...sourceFieldInfo,
          codedValues:
            sourceFieldInfo.codedValues != undefined && sourceFieldInfo.codedValues != null
              ? sourceFieldInfo.codedValues
              : f.codedValues, //CONFIG DESCRIPTION knows better
          editable:
            sourceFieldInfo.editable != undefined && sourceFieldInfo.editable != null
              ? sourceFieldInfo.editable
              : f.type != 'geometry' && sourceDescription.idField != f.name, //CONFIG DESCRIPTION knows better
          length: f.length != undefined ? f.length : sourceFieldInfo.length, //SERVICE knows better
          fieldName: f.name, //SERVICE knows better
          nullable:
            f.name == sourceDescription.idField
              ? true
              : sourceFieldInfo.nullable !== undefined
                ? sourceFieldInfo.nullable
                : f.nullable, //CONFIG DESCRIPTION knows better
          type: f.type, //SERVICE knows better
          geometryType: f.geometryType, // Service knows better
          z: sourceFieldInfo.z || 0
        }
      })
      for (const sourceFieldName in sourceDescription.fieldInfos) {
        if (sourceDescription.fieldInfos[sourceFieldName].forceAdd && !fields[sourceFieldName]) {
          const sourceFieldInfo = sourceDescription.fieldInfos[sourceFieldName]
          fields[sourceFieldName] = {
            ...sourceFieldInfo,
            fieldName: sourceFieldName,
            geometryType: sourceFieldInfo.geometryType || GeometryType.None,
            z: sourceFieldInfo.z || 0
          }
        }
      }
    }

    return fields
  }

  public createImageLayers(
    namespacedSourceDescriptions: {[namespaceUrl: string]: IFeatureLayerInfos},
    getStore: () => IStore
  ): Array<TileLayer | ImageLayer> {
    const layers: Array<TileLayer | ImageLayer> = []
    const getImageSchemeZ = (sourceDesc: ISourceDescription) => {
      for (const key in sourceDesc.pseudoViewSchema) {
        if (
          sourceDesc.pseudoViewSchema[key] == ViewSchemaType.Image ||
          sourceDesc.pseudoViewSchema[key] == ViewSchemaType.TileImage
        ) {
          return key
        }
      }
      throw new Error('there is no image view schema')
    }
    for (const namespaceUrl in namespacedSourceDescriptions) {
      const layersArray = {
        tile: {},
        image: {},
        xyz: {}
      } as {[key: string]: {[z: string]: IFeatureLayerInfo[]}}
      for (const layerUrl in namespacedSourceDescriptions[namespaceUrl]) {
        if (!namespacedSourceDescriptions[namespaceUrl][layerUrl].sourceDescription.addOnMap) {
          continue
        }
        if (namespacedSourceDescriptions[namespaceUrl][layerUrl].sourceDescription.imageViewType == 'tile') {
          const sourceViewSchemaZ = getImageSchemeZ(
            namespacedSourceDescriptions[namespaceUrl][layerUrl].sourceDescription
          )
          if (!layersArray.tile[sourceViewSchemaZ]) {
            layersArray.tile[sourceViewSchemaZ] = []
          }
          layersArray.tile[sourceViewSchemaZ].push(namespacedSourceDescriptions[namespaceUrl][layerUrl])
        } else if (namespacedSourceDescriptions[namespaceUrl][layerUrl].sourceDescription.imageViewType == 'image') {
          const sourceViewSchemaZ = getImageSchemeZ(
            namespacedSourceDescriptions[namespaceUrl][layerUrl].sourceDescription
          )
          if (!layersArray.image[sourceViewSchemaZ]) {
            layersArray.image[sourceViewSchemaZ] = []
          }
          layersArray.image[sourceViewSchemaZ].push(namespacedSourceDescriptions[namespaceUrl][layerUrl])
        } else if (namespacedSourceDescriptions[namespaceUrl][layerUrl].sourceDescription.imageViewType == 'xyz') {
          const sourceViewSchemaZ = getImageSchemeZ(
            namespacedSourceDescriptions[namespaceUrl][layerUrl].sourceDescription
          )
          if (!layersArray.xyz[sourceViewSchemaZ]) {
            layersArray.xyz[sourceViewSchemaZ] = []
          }
          layersArray.xyz[sourceViewSchemaZ].push(namespacedSourceDescriptions[namespaceUrl][layerUrl])
        }
      }
      for (const type in layersArray) {
        for (const z in layersArray[type]) {
          let maxRes = 0
          let minRes = 0
          const layerInfo = layersArray[type][z][0]
          for (const zLevel in layerInfo.viewSchema) {
            //they should be all like the first (layersArray[type][z][0])
            const intZLevel = parseInt(zLevel)
            if (
              getResolutions()[intZLevel] &&
              (layerInfo.viewSchema[intZLevel] == ViewSchemaType.Image ||
                layerInfo.viewSchema[intZLevel] == ViewSchemaType.TileImage)
            ) {
              if (!maxRes) {
                if (intZLevel == 0) {
                  maxRes = getResolutions()[intZLevel] + 0.001
                } else {
                  maxRes = getResolutions()[intZLevel - 1] - 0.002
                }
              }
              minRes = getResolutions()[intZLevel] - 0.001
            }
          }

          layers.push(
            this.createImageLayer(layersArray[type][z], minRes, maxRes, type as 'tile' | 'image' | 'xyz', getStore)
          )
        }
      }
    }
    return layers
  }

  public abstract createImageLayer(
    layerInfos: IFeatureLayerInfo[],
    minRes: number,
    maxRes: number,
    type: 'tile' | 'image' | 'xyz',
    getStore: () => IStore
  ): TileLayer | ImageLayer

  protected createFieldDescriptionsFromSourceDesc(sourceDesc: ISourceDescription): FieldDescription[] {
    const result: FieldDescription[] = []
    for (const key in sourceDesc.fieldInfos) {
      const fd: FieldDescription = {
        geometryType: sourceDesc.fieldInfos[key].geometryType!,
        name: key,
        nullable: sourceDesc.fieldInfos[key].nullable!,
        type: sourceDesc.fieldInfos[key].type!,
        length: sourceDesc.fieldInfos[key].length!
      }
    }

    return result
  }

  protected createFeatureLayerInfo(
    layerToFieldDescriptions: {[layerUrl: string]: Array<FieldDescription>},
    sourceDescriptions: ISourceDescriptions
  ): Array<IFeatureLayerInfo | FailedServiceInfo> {
    const result: Array<IFeatureLayerInfo | FailedServiceInfo> = []
    for (const layerUrl in sourceDescriptions) {
      const layerName = sourceDescriptions[layerUrl].layerName
      const glueUrl = `${sourceDescriptions[layerUrl].namespacedUrl}/${layerName}`
      if (layerToFieldDescriptions[glueUrl]) {
        result.push(
          new FeatureLayerInfo(
            this.getFieldsInfo(layerToFieldDescriptions[glueUrl], sourceDescriptions[layerUrl]),
            sourceDescriptions[layerUrl]
          )
        )
      } else if (sourceDescriptions[layerUrl].nonserver) {
        result.push(
          new FeatureLayerInfo(
            this.getFieldsInfo(
              this.createFieldDescriptionsFromSourceDesc(sourceDescriptions[layerUrl]),
              sourceDescriptions[layerUrl]
            ),
            sourceDescriptions[layerUrl]
          )
        )
      } else {
        result.push(
          new FailedServiceInfo(sourceDescriptions[layerUrl], new Error('Missing layerUrl in layerToFieldDescriptions'))
        )
      }
    }
    return result
  }

  protected createFailedLayerInfo(
    error: Error,
    errorCode: number,
    sourceDescriptions: ISourceDescriptions
  ): Array<DeniedServiceInfo | FailedServiceInfo> {
    const result: Array<DeniedServiceInfo | FailedServiceInfo> = []
    for (const sourceUrl in sourceDescriptions) {
      if (errorCode == 401 || errorCode == 403) {
        result.push(new DeniedServiceInfo(sourceDescriptions[sourceUrl], error))
      } else {
        result.push(new FailedServiceInfo(sourceDescriptions[sourceUrl], error))
      }
    }
    return result
  }

  protected resolveNamespacePromises(
    namespaceInfoPromises: Array<Promise<Array<IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo>>>
  ): Promise<Array<IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo>> {
    return new Promise((resolve, reject) => {
      Promise.all(namespaceInfoPromises).then(r => {
        let allNamespacesResults: Array<IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo> = []
        r.forEach(f => {
          allNamespacesResults = allNamespacesResults.concat(f)
        })
        resolve(allNamespacesResults)
      })
    })
  }

  protected symHeight: number = 40
  protected symWidth: number = 40
  protected ensureVectorRenderMap(mapId: string): Map {
    const vectorRenderMapTmpId = mapId
    if (!document.getElementById(vectorRenderMapTmpId)) {
      const body = document.getElementsByTagName('body')[0]
      const node = document.createElement('div')
      node.style.position = 'absolute'

      //
      // node.style.bottom = "0"
      node.style.opacity = '0'
      node.style.zIndex = '-9'
      node.style.width = this.symWidth + 'px'
      node.style.height = this.symHeight + 'px'
      node.id = vectorRenderMapTmpId
      body.appendChild(node)
    }
    return new Map({
      target: vectorRenderMapTmpId,
      view: new View({
        projection: CoordinateSystem.WEB_MERCATOR,
        center: [0, 0],
        zoom: 17
      })
    })
  }

  protected getLegendFeature(layerInfo: IFeatureLayerInfo): Feature {
    const feat = createNewFeature(layerInfo)
    let geom: Geometry
    if (layerInfo.getGeometryType() == GeometryType.Point) {
      geom = new Point([0, 0])
    } else if (
      layerInfo.getGeometryType() == GeometryType.MultiLineString ||
      layerInfo.getGeometryType() == GeometryType.LineString
    ) {
      geom = new LineString([
        [-30, 0],
        [30, 0]
      ])
    } else {
      geom = new Polygon([
        [
          [-17, -12],
          [17, -12],
          [17, 12],
          [-17, 12]
        ]
      ])
    }
    feat.setGeometry(geom)

    return feat
  }

  protected renderFeature(
    layerInfo: IFeatureLayerInfo,
    feature: Feature,
    name: string,
    style: ((feature: Feature, resulution: number) => Style | Style[]) | null
  ): Promise<IImageLegendRowInfo> {
    return new Promise((resolve, reject) => {
      const mapDivId = layerInfo.url + uuidv4()
      const vectorRenderingMap = this.ensureVectorRenderMap(mapDivId)
      const source = new VectorSource({})
      const vectorLayer = new VectorLayer({source: source})

      vectorLayer.setStyle(style as any)
      vectorLayer.getSource().addFeature(feature)
      vectorRenderingMap.addLayer(vectorLayer)
      setTimeout(() => {
        mapToBlob(vectorRenderingMap).then(blob => {
          const reader = new FileReader()
          reader.readAsDataURL(blob)
          reader.onloadend = () => {
            resolve({
              base64: reader.result as string,
              width: this.symWidth,
              height: this.symHeight,
              label: name
            })

            const body = document.getElementsByTagName('body')[0]
            const map = document.getElementById(mapDivId)
            body.removeChild(map!)
          }
        })
      }, 100)
    })
  }

  public renderVectorSymbology(
    layerInfo: IFeatureLayerInfo,
    style: ((feature: Feature, resulution: number) => Style | Style[]) | null
  ): Promise<IImageLegendRowInfo[]> {
    return new Promise((resolve, reject) => {
      if (layerInfo.getGeometryType() == GeometryType.None) {
        resolve([])
        return
      }
      const promises: Array<Promise<IImageLegendRowInfo>> = []
      if (layerInfo.sourceDescription.vectorRenderer && layerInfo.sourceDescription.vectorRenderer.nomenclature) {
        const codedValues = layerInfo.fields[layerInfo.sourceDescription.vectorRenderer.nomenclature.field].codedValues!
        const listedOnly = layerInfo.sourceDescription.vectorRenderer!.nomenclature!.listedOnly
        if (layerInfo.sourceDescription.vectorRenderer!.nomenclature!.values) {
          codedValues.forEach(c => {
            if (
              (listedOnly && layerInfo.sourceDescription.vectorRenderer!.nomenclature!.values![c.code.toString()]) ||
              !listedOnly
            ) {
              promises.push(
                new Promise((resolve, reject) => {
                  const feature = this.getLegendFeature(layerInfo)
                  feature.set(layerInfo.sourceDescription.vectorRenderer!.nomenclature!.field, c.code)
                  resolve(this.renderFeature(layerInfo, feature, c.name, style))
                })
              )
            }
          })
        }
        if (layerInfo.sourceDescription.vectorRenderer!.nomenclature!.ins) {
          const ins = layerInfo.sourceDescription.vectorRenderer!.nomenclature!.ins!
          ins.forEach(inn => {
            codedValues.forEach(c => {
              promises.push(
                new Promise((resolve, reject) => {
                  const feature = this.getLegendFeature(layerInfo)
                  feature.set(layerInfo.sourceDescription.vectorRenderer!.nomenclature!.field, c.code)
                  resolve(this.renderFeature(layerInfo, feature, inn.title, style))
                })
              )
            })
          })
        }
        if (layerInfo.sourceDescription.vectorRenderer!.def) {
          promises.push(
            new Promise((resolve, reject) => {
              const feature = this.getLegendFeature(layerInfo)
              feature.set(layerInfo.sourceDescription.vectorRenderer!.nomenclature!.field, uuidv4())
              resolve(this.renderFeature(layerInfo, feature, '<Други>', style))
            })
          )
        }
      } else {
        promises.push(
          new Promise((resolve, reject) => {
            resolve(this.renderFeature(layerInfo, this.getLegendFeature(layerInfo), '', style))
          })
        )
      }

      Promise.all(promises).then(r => {
        resolve(r)
      })
    })
  }
}
